import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * One calendar month of the window.
 *
 * Every figure carries the ledger's own sign — `withdrawalsUsdt` is negative,
 * and so is a month whose ticket returns exceeded its uses — so the columns add
 * up as read. `netUserCashUsdt` is `deposits + withdrawals`, not a magnitude
 * difference.
 *
 * `rakeUsdt` is HOUSE-WIDE even when the panel is cohort-filtered, matching the
 * rake rows in `byType`: rake credits the platform's own account, so any cohort
 * scoping renders it 0.00.
 *
 * `freeTicketsUsedUsdt` comes from the ticket ledger, not the balance ledger — a
 * ticket-funded entry debits nobody, so no movement exists to measure.
 */
export const ArenaOpsMoneyMonthSchema = z.object({
  /** UTC first instant of the calendar month. */
  monthStart: z.iso.datetime(),
  /** The slice of the month the selected range actually covers — `[coveredFrom, coveredTo)`. */
  coveredFrom: z.iso.datetime(),
  coveredTo: z.iso.datetime(),
  /** True when the range covers less than the whole calendar month. The row is kept and labelled, never dropped or silently short. */
  isPartial: z.boolean(),
  depositsUsdt: ArenaOpsUsdtSchema,
  withdrawalsUsdt: ArenaOpsUsdtSchema,
  netUserCashUsdt: ArenaOpsUsdtSchema,
  /** ADMIN_CREDIT net of ADMIN_DEBIT; `byType` keeps each gross. */
  adminCreditUsdt: ArenaOpsUsdtSchema,
  freeTicketsUsedUsdt: ArenaOpsUsdtSchema,
  rewardsPaidUsdt: ArenaOpsUsdtSchema,
  rakeUsdt: ArenaOpsUsdtSchema,
  spinAndGoSpreadUsdt: ArenaOpsUsdtSchema,
});
export type ArenaOpsMoneyMonth = z.infer<typeof ArenaOpsMoneyMonthSchema>;

/**
 * P1 — net movement per type over the window, from the LAG delta.
 *
 * `months` is the same measure bucketed by calendar month, ascending and
 * gap-free: a month the range covers but nothing moved in is returned at zero,
 * because the aggregate is complete over the range by construction.
 *
 * `cashInUsdt` / `cashOutUsdt` / `netUsdt` are summed from the ROUNDED months,
 * so the headline is reachable by adding the column on screen. `byType` is a
 * different grain — one row per movement type, against the months' type GROUPS
 * — and rounds independently, so it can sit a cent off the same figure derived
 * from the months. Reconcile the headline against the months, not against
 * `byType`.
 */
export const ArenaOpsMoneyPeriodFlowsResponseSchema = z.object({
  months: z.array(ArenaOpsMoneyMonthSchema),
  byType: z.array(z.object({ type: z.string(), movementCount: z.number().int(), netUsdt: ArenaOpsUsdtSchema })),
  cashInUsdt: ArenaOpsUsdtSchema,
  cashOutUsdt: ArenaOpsUsdtSchema,
  netUsdt: ArenaOpsUsdtSchema,
});
export type ArenaOpsMoneyPeriodFlowsResponse = z.infer<typeof ArenaOpsMoneyPeriodFlowsResponseSchema>;
